/*
  Admin › History (30) — wave 1: ideas 3 (search + filter chips: Date (default Last 14 days), Person,
  Area incl. Shop Drawings, Action, Status; Time / Project / Person grouping), 4 (loads only when this tab
  opens, search waits 300 ms, row-shaped skeleton, "Showing the 200 most recent changes (since …)" with
  Load older via date_to) and 7 (no Status column — one "All N restorable" line in the table footer, a
  badge only on the exceptions).
  Wave 2: ideas 1 (a feed by day in plain sentences: "Updated execution structure — Luxor Tower", times in
  GST, a Size column), 2 (bursts — the same person in the same area, each change within 15 minutes of the
  next — fold into one row that opens), 5 (summary strip: changes, people, a bar per area and the most
  active people; each part filters) and 6 (a details drawer beside the feed: badges, structure changes
  grouped by floor › panel, technical fields hidden, "Lock restore point" and "Undo this change…").
  Wave 3: ideas 8 (before → after: "Theme: White / Navy / Green → Sara Light" under each change and every
  changed field in the drawer — secrets are never shown) and 9 ("Pattern spotted" — churn such as importing
  and then deleting BOQ lines again and again, with a link to those changes; entries that only carry a project
  id get the project's name, so "Project" grouping works for Procurement too).
  Final QA: the summary strip counts the whole period on the server (GET /admin/history/insights total / areas /
  people, the feed's own window) — "Loaded so far" only when no period total applies; structure changes read as
  their summary, never "38 items → 38 items"; the inline drawer from 1440px (a side panel below), Esc closes it
  wherever the focus is, and its footer stays on screen; floor boxes list one line per kind of device.
*/
import { createContext, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRight, ChevronDown, ChevronRight, FolderKanban, History as HistoryIcon, Lock, Package, PenTool, Plus, RotateCcw, SearchX,
  Settings2, ShoppingCart, Sparkles, X,
} from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatDate, formatDateTime, formatNumber, formatTime, toDateInputValue } from '@/lib/format';
import { getErrorMessage } from '@/lib/errors';
import { toneClass } from '@/lib/status';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useWorkspace } from '@/contexts/AppSettingsContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FilterChip } from '@/components/ui/filter-chip';
import { EmptyState } from '@/components/ui/empty-state';
import { StatusPill } from '@/components/ui/status-pill';
import { SidePanel } from '@/components/ui/side-panel';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { notify } from '@/components/ui/notify';
import { SearchBox, Segmented, UpperLabel, UserAvatar } from '@/components/admin/AdminUi';
import {
  HISTORY_ACTIONS, HISTORY_AREAS, HISTORY_AREA_TONES, HISTORY_DATE_RANGES, HISTORY_PAGE_SIZE, HISTORY_STATUSES,
  burstHistory, burstSummary, durationWords, groupFloorLines, groupHistory, groupHistorySteps, historyAreaLabel, historyBreakdown,
  historyCsv, historyQueryParams, historyRangeStart, historySentence, historySize, historyStatusSummary, humanizeChangeSummary,
  insightsBreakdown, isRecordId, mergeHistoryPages, optionLabel, parseChangeCounts, parseStructureSummary, collectionNoun,
  historyValueLine, historyValueText, patternText, withProjectNames,
} from '@/components/admin/adminModel';

// 30 #8 — before → after values per entry id ({ fields, more }), read by every feed row.
const HistoryValuesContext = createContext({});
const VALUES_BATCH = 200;
const INSIGHT_DAYS = { '7d': 7, '14d': 14, '30d': 30, '90d': 90 };

const STATUS_TONES = { available: 'green', undone: 'blue', canceled: 'gray', superseded: 'amber', partial: 'amber' };
const statusLabel = (status) => optionLabel(HISTORY_STATUSES, status) || status;
const AREA_ICONS = { project: FolderKanban, procurement: ShoppingCart, inventory: Package, admin: Settings2, shop_drawings: PenTool };
const AREA_ICON_TONES = { project: 'blue', procurement: 'navy', inventory: 'teal', admin: 'violet', shop_drawings: 'amber' };

function HistoryStatus({ status, always = false }) {
  if (!status || (status === 'available' && !always)) return null;
  return <StatusPill tone={STATUS_TONES[status] || 'gray'} label={statusLabel(status)} size="sm" className="shrink-0" />;
}

function AreaTag({ area }) {
  return (
    <span className="inline-flex h-[22px] items-center whitespace-nowrap rounded-md border border-line bg-surface-2 px-1.5 text-[11.5px] font-semibold text-ink-2">
      {historyAreaLabel(area)}
    </span>
  );
}

/** "+ Area" chip that opens a short menu; set → "Area: Procurement ×". */
function ChipMenu({ label, value, options, onChange, allLabel, testId }) {
  const current = options.find((option) => option.value === value);
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <FilterChip label={label} value={current?.label || null} onClear={() => onChange('')} data-testid={testId} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-[320px] min-w-[200px] overflow-y-auto rounded-xl border-line bg-surface p-1.5 text-ink shadow-overlay">
        {options.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onSelect={() => onChange(option.value)}
            className={cn('h-[34px] rounded-lg text-[13px]', option.value === value && 'font-semibold text-brand-ink')}
          >
            {option.label}
          </DropdownMenuItem>
        ))}
        {allLabel ? (
          <>
            <DropdownMenuSeparator className="bg-line-2" />
            <DropdownMenuItem onSelect={() => onChange('')} className="h-[34px] rounded-lg text-[13px] text-ink-2">{allLabel}</DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* --------------------------------------------------------------------------------------------- */
/**
 * 30 #5 — summary strip: how much changed, where, and by whom. Every part filters the feed.
 * period: the whole period counted on the server (insightsBreakdown) — or null, then the loaded rows are counted
 * and the strip says what it counts ("Loaded so far" while older changes are not loaded).
 */
function SummaryStrip({ items, label, period, since, filters, sliceLabel, onArea, onPerson }) {
  const loaded = useMemo(() => historyBreakdown(items), [items]);
  const breakdown = period || loaded;
  if (!items.length && !period?.total) return null;
  const oldest = items[items.length - 1];
  const sinceText = formatDate(period ? since : oldest?.created_at, { weekday: true, year: 'auto' }).replace(',', '');
  const sinceSuffix = sinceText && sinceText !== '—' ? ` · since ${sinceText}` : '';
  // A person or an area picked here (or "Changes · 14 days" on Users): the headline is that slice of the period —
  // "82 of 506 changes · Sayed" — the number the feed shows; the bars and people below stay the period's context.
  const slice = !period ? null
    : filters.person ? { count: breakdown.people.find((person) => person.id === filters.person)?.count || 0 }
      : filters.area ? { count: breakdown.areas.find((area) => area.key === filters.area)?.count || 0 }
        : null;
  const top = breakdown.people.slice(0, 3);
  const maxPerson = top[0]?.count || 1;
  return (
    <section className="flex flex-wrap items-center gap-x-7 gap-y-4 rounded-card border border-line bg-surface px-5 py-4 shadow-card" aria-label="Summary of these changes" data-testid="history-summary" data-scope={period ? 'period' : 'loaded'} data-slice={slice ? (filters.person ? 'person' : 'area') : undefined}>
      <div className="min-w-[170px]">
        <UpperLabel>{label}</UpperLabel>
        <p className="mt-0.5 flex items-baseline gap-1.5">
          <span className="font-heading text-[26px] font-black leading-none tabular-nums text-ink" data-testid="history-summary-total">
            {formatNumber(slice ? slice.count : breakdown.total)}
          </span>
          <span className="text-[13px] text-ink-2">
            {slice
              ? `of ${formatNumber(breakdown.total)} ${breakdown.total === 1 ? 'change' : 'changes'}`
              : breakdown.total === 1 ? 'change' : 'changes'}
          </span>
        </p>
        <p className="mt-1 text-[12px] text-ink-3" data-testid="history-summary-people">
          {slice
            ? <><b className="font-semibold text-ink-2">{sliceLabel || 'This filter'}</b>{sinceSuffix}</>
            : `by ${breakdown.people.length} ${breakdown.people.length === 1 ? 'person' : 'people'}${sinceSuffix}`}
        </p>
      </div>
      <span className="hidden h-12 w-px bg-line sm:block" aria-hidden="true" />
      <div className="min-w-[240px] flex-1">
        <UpperLabel>By area</UpperLabel>
        <div className="mt-2 flex h-2.5 overflow-hidden rounded-full bg-surface-2" aria-hidden="true">
          {breakdown.areas.map((area) => (
            <span
              key={area.key || 'other'}
              className={cn('h-full', toneClass(HISTORY_AREA_TONES[area.key] || 'gray'))}
              style={{ width: `${Math.max(area.share * 100, 1.5)}%`, background: 'var(--tone)' }}
              title={`${area.label}: ${area.count}`}
            />
          ))}
        </div>
        <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-ink-2">
          {breakdown.areas.map((area) => {
            const active = filters.area === area.key;
            const content = (
              <>
                <span className={cn('ui-tone-dot h-2 w-2', toneClass(HISTORY_AREA_TONES[area.key] || 'gray'))} aria-hidden="true" />
                {area.label} <b className="font-bold tabular-nums text-ink">{formatNumber(area.count)}</b>
              </>
            );
            return (
              <li key={area.key || 'other'}>
                {area.key ? (
                  <button
                    type="button"
                    onClick={() => onArea(active ? '' : area.key)}
                    aria-pressed={active}
                    className={cn('inline-flex items-center gap-1.5 rounded px-0.5 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', active && 'font-semibold text-ink underline')}
                    data-testid={`history-summary-area-${area.key}`}
                  >
                    {content}
                  </button>
                ) : (
                  // older entries recorded no area: counted, but there is no filter that finds them
                  <span className="inline-flex items-center gap-1.5 px-0.5" title="Older changes recorded without an area" data-testid="history-summary-area-other">
                    {content}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </div>
      <span className="hidden h-12 w-px bg-line lg:block" aria-hidden="true" />
      <ul className="w-full min-w-[220px] space-y-1 lg:w-[260px]" aria-label="Most active people">
        {top.map((person) => {
          const active = person.id && filters.person === person.id;
          return (
            <li key={person.id || person.name}>
              <button
                type="button"
                onClick={() => person.id && onPerson(active ? '' : person.id)}
                disabled={!person.id}
                aria-pressed={Boolean(active)}
                className={cn('grid w-full grid-cols-[22px_minmax(0,110px)_minmax(0,1fr)_auto] items-center gap-2 rounded text-left text-[12.5px] hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', active && 'font-semibold')}
                data-testid={`history-summary-person-${person.id || 'unknown'}`}
              >
                <UserAvatar name={person.name} seed={person.id || person.name} size={20} />
                <span className="truncate text-ink">{person.name}</span>
                <span className="h-1.5 overflow-hidden rounded-full bg-surface-2" aria-hidden="true">
                  <span className="block h-full rounded-full bg-[color:var(--status-navy)] opacity-60" style={{ width: `${(person.count / maxPerson) * 100}%` }} />
                </span>
                <b className="font-bold tabular-nums text-ink">{formatNumber(person.count)}</b>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/* --------------------------------------------------------------------------------------------- */
function Sentence({ item, className }) {
  const { lead, target } = historySentence(item);
  return (
    <span className={cn('min-w-0 text-[13px] text-ink', className)}>
      {lead}
      {target ? <> — <b className="font-semibold">{target}</b></> : null}
    </span>
  );
}

const shortName = (name, compact) => (compact ? String(name || '—').trim().split(/\s+/)[0] : name || '—');

/** Value rows that say something (an older server could still send "38 items → 38 items"). */
const telling = (fields = []) => fields.filter((field) => field.kind !== 'updated' || historyValueText(field.before) !== historyValueText(field.after));

function FeedRow({ item, selected, onOpen, child = false, timeLabel, compact = false }) {
  const summary = humanizeChangeSummary(item.change_summary);
  const values = useContext(HistoryValuesContext)[item.id];
  const workspace = useWorkspace();
  // structure edits read as their summary ("52 structure changes on 3RD PODIUM, 4TH PODIUM…"), not a field value
  const structure = Boolean(parseStructureSummary(item.change_summary));
  const fields = structure ? [] : telling(values?.fields);
  const first = fields[0];
  const valueLine = first ? historyValueLine(first, { timeZone: workspace.time_zone }) : '';
  const moreValues = first ? fields.length - 1 + (values?.more || 0) : 0;
  return (
    <tr
      tabIndex={0}
      aria-selected={selected}
      onClick={() => onOpen(item)}
      onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onOpen(item); } }}
      className={cn(
        'grid cursor-pointer grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 border-b border-line-2 px-4 py-2 outline-none transition-colors last:border-b-0 hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring md:table-row md:p-0',
        selected && 'bg-brand-soft hover:bg-brand-soft',
        child && 'bg-surface-2/60'
      )}
      data-testid={`history-row-${item.id}`}
    >
      <td className={cn('text-[13px] tabular-nums text-ink md:h-[52px] md:px-4 md:align-middle', selected && 'md:shadow-[inset_3px_0_0_var(--brand)]')}>
        {timeLabel ?? formatTime(item.created_at, { timeZone: workspace.time_zone })}
      </td>
      <td className="min-w-0 md:px-3 md:align-middle">
        <span className="flex min-w-0 items-center gap-2">
          <UserAvatar name={item.user_name} seed={item.user_id || item.user_name} size={22} />
          <span className="truncate text-[13px] text-ink" title={item.user_name}>{shortName(item.user_name, compact)}</span>
        </span>
      </td>
      <td className={cn('col-span-3 row-start-2 min-w-0 pt-1 md:px-3 md:py-2 md:align-middle', child && 'md:pl-9')}>
        <span className="flex min-w-0 items-center gap-2">
          <Sentence item={item} className="truncate" />
          <HistoryStatus status={item.status} />
        </span>
        <span className="flex min-w-0 items-center gap-1.5">
          {compact ? <AreaTag area={item.page_type} /> : null}
          {valueLine ? (
            <span className="block truncate text-[12px] text-ink-2" title={fields.map((field) => historyValueLine(field, { timeZone: workspace.time_zone })).join('\n')} data-testid={`history-values-${item.id}`}>
              {valueLine}
              {moreValues > 0 ? <span className="text-ink-3"> · +{moreValues} more</span> : null}
            </span>
          ) : summary ? <span className="block truncate text-[12px] text-ink-3" title={item.change_summary}>{summary}</span> : null}
        </span>
      </td>
      {compact ? null : <td className="hidden px-3 align-middle md:table-cell"><AreaTag area={item.page_type} /></td>}
      <td className="hidden truncate whitespace-nowrap px-3 text-right align-middle text-[12.5px] tabular-nums text-ink-2 md:table-cell" title={historySize(item)} data-testid={`history-size-${item.id}`}>{historySize(item)}</td>
      <td className="col-start-3 row-start-1 text-right md:pr-3 md:align-middle">
        <ChevronRight className="inline h-3.5 w-3.5 text-ink-4" aria-hidden="true" />
      </td>
    </tr>
  );
}

function BurstRows({ row, open, onToggle, selectedId, onOpen, compact = false }) {
  const { items } = row;
  const newest = items[0];
  const oldest = items[items.length - 1];
  const { text, targets, joiner } = burstSummary(items);
  const span = Math.abs(Date.parse(newest.created_at) - Date.parse(oldest.created_at));
  const sub = targets.length > 1
    ? `${targets.slice(0, 4).join(', ')}${targets.length > 4 ? ` +${targets.length - 4} more` : ''}`
    : `${targets[0] ? `${targets[0]} · ` : ''}in ${durationWords(span)}`;
  const panelId = `burst-${newest.id}`;
  return (
    <>
      <tr
        className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 border-b border-line-2 px-4 py-2 md:table-row md:p-0"
        data-testid={`history-burst-${newest.id}`}
      >
        <td className="text-[13px] tabular-nums text-ink md:h-[52px] md:px-4 md:align-middle">
          <span className="whitespace-nowrap leading-tight">{formatTime(oldest.created_at)}<br /><span className="text-[11.5px] text-ink-3">to {formatTime(newest.created_at)}</span></span>
        </td>
        <td className="min-w-0 md:px-3 md:align-middle">
          <span className="flex min-w-0 items-center gap-2">
            <UserAvatar name={newest.user_name} seed={newest.user_id || newest.user_name} size={22} />
            <span className="truncate text-[13px] text-ink" title={newest.user_name}>{shortName(newest.user_name, compact)}</span>
          </span>
        </td>
        <td className="col-span-3 row-start-2 min-w-0 pt-1 md:px-3 md:py-2 md:align-middle">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            aria-controls={panelId}
            className="group flex w-full min-w-0 items-start gap-2 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid={`history-burst-toggle-${newest.id}`}
          >
            <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-md border border-line bg-surface text-ink-3 group-hover:text-ink">
              <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-90')} aria-hidden="true" />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-[13px] text-ink" title={`${text}${targets.length === 1 && joiner ? ` ${joiner} ${targets[0]}` : ''}`}>
                <b className="font-semibold">{text}</b>
                {targets.length === 1 && joiner ? <> {joiner} <b className="font-semibold">{targets[0]}</b></> : null}
              </span>
              <span className="flex min-w-0 items-center gap-1.5">
                {compact ? <AreaTag area={newest.page_type} /> : null}
                <span className="block truncate text-[12px] text-ink-3">{sub}</span>
              </span>
            </span>
          </button>
        </td>
        {compact ? null : <td className="hidden px-3 align-middle md:table-cell"><AreaTag area={newest.page_type} /></td>}
        <td className="hidden px-3 text-right align-middle text-[12.5px] tabular-nums text-ink-2 md:table-cell">{items.length} changes</td>
        <td className="col-start-3 row-start-1 md:pr-3" />
      </tr>
      {open ? items.map((item) => (
        <FeedRow key={item.id} item={item} selected={selectedId === item.id} onOpen={onOpen} child compact={compact} />
      )) : null}
    </>
  );
}

/** The feed — memoised so typing in the search box doesn't re-render 200 rows per key. */
const HistoryRows = memo(function HistoryRows({ groups, groupBy, selectedId, openBursts, onToggleBurst, onOpen, compact }) {
  return (
    <>
      {groups.map((group) => {
        const rows = groupBy === 'time' ? burstHistory(group.items) : group.items.map((item) => ({ type: 'item', key: item.id, item }));
        return (
          <tbody key={group.key} className="block md:table-row-group" data-testid="history-group">
            <tr className="block border-b border-line-2 bg-surface-2 md:table-row">
              <th scope="rowgroup" colSpan={compact ? 5 : 6} className="block px-4 py-1.5 text-left text-[12.5px] font-normal text-ink-3 md:table-cell md:h-8 md:py-0">
                <span className="font-bold text-ink">{group.label}</span> · {group.items.length} {group.items.length === 1 ? 'change' : 'changes'}
              </th>
            </tr>
            {rows.map((row) => (row.type === 'burst' ? (
              <BurstRows
                key={row.key}
                row={row}
                open={openBursts.has(row.key)}
                onToggle={() => onToggleBurst(row.key)}
                selectedId={selectedId}
                onOpen={onOpen}
                compact={compact}
              />
            ) : (
              <FeedRow
                key={row.key}
                item={row.item}
                selected={selectedId === row.item.id}
                onOpen={onOpen}
                compact={compact}
                timeLabel={groupBy === 'time' ? undefined : (
                  <span className="leading-tight">{formatDate(row.item.created_at, { year: 'auto' })}<br /><span className="text-ink-3">{formatTime(row.item.created_at)}</span></span>
                )}
              />
            )))}
          </tbody>
        );
      })}
    </>
  );
});

/** 30 #9 — "Pattern spotted": the biggest churn of the period, with a link to exactly those changes. */
function PatternCallout({ patterns = [], onShow }) {
  const [showAll, setShowAll] = useState(false);
  if (!patterns.length) return null;
  const list = showAll ? patterns : patterns.slice(0, 1);
  return (
    <section
      className="rounded-card border border-brand-soft-border bg-brand-soft px-4 py-3 sm:px-5"
      aria-label="Patterns spotted in these changes"
      data-testid="history-patterns"
    >
      <ul className="space-y-2.5">
        {list.map((pattern) => {
          const text = patternText(pattern);
          return (
            <li key={`${pattern.kind}-${pattern.user_id}-${pattern.day}-${pattern.collection || ''}`} className="flex flex-wrap items-start gap-x-3 gap-y-2" data-testid="history-pattern">
              <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-brand-ink" aria-hidden="true" />
              <p className="min-w-[240px] flex-1 text-[13px] leading-relaxed text-ink">
                <b className="font-bold">Pattern spotted</b> — {text.lead}{text.detail ? ` (${text.detail})` : ''}.
                <span className="text-ink-3"> {text.advice}</span>
              </p>
              <button
                type="button"
                onClick={() => onShow(pattern)}
                className="inline-flex items-center gap-1 self-center whitespace-nowrap rounded text-[13px] font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="history-pattern-show"
              >
                Show these changes <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </li>
          );
        })}
      </ul>
      {patterns.length > 1 ? (
        <button
          type="button"
          onClick={() => setShowAll((current) => !current)}
          aria-expanded={showAll}
          className="mt-2 inline-flex items-center gap-1 pl-7 text-[12.5px] font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid="history-patterns-more"
        >
          {showAll ? 'Show fewer' : `${patterns.length - 1} more ${patterns.length - 1 === 1 ? 'pattern' : 'patterns'}`}
          <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', showAll && 'rotate-180')} aria-hidden="true" />
        </button>
      ) : null}
    </section>
  );
}

/** 30 #8 — every changed field with its value before and after (the drawer). */
function ValueChanges({ values, timeZone }) {
  const fields = telling(values?.fields);
  if (!fields.length) return null;
  return (
    <div className="space-y-2" data-testid="history-detail-values">
      <UpperLabel>Before → after</UpperLabel>
      <ul className="divide-y divide-line-2 rounded-xl border border-line">
        {fields.map((field, index) => (
          <li key={`${field.field}-${index}`} className="px-3 py-2 text-[12.5px]">
            <span className="block font-semibold text-ink">
              {field.label}
              {field.record && !['record', 'global'].includes(field.record) && !isRecordId(field.record) && !field.label.startsWith(field.record) ? (
                <span className="font-normal text-ink-3"> · {field.record}</span>
              ) : null}
            </span>
            {field.kind === 'secret' ? (
              <span className="text-ink-3">Changed — the value is never shown</span>
            ) : field.kind === 'added' || field.kind === 'removed' ? (
              <StatusPill tone={field.kind === 'added' ? 'green' : 'red'} label={field.kind === 'added' ? 'Added' : 'Removed'} size="sm" hideDot />
            ) : (
              <span className="flex min-w-0 flex-wrap items-center gap-x-1.5 text-ink-2">
                <span className="text-ink-3 line-through decoration-[color:var(--ink-4)]">{historyValueText(field.before, { timeZone })}</span>
                <ArrowRight className="h-3 w-3 shrink-0 text-ink-4" aria-hidden="true" />
                <span className="font-semibold text-ink">{historyValueText(field.after, { timeZone })}</span>
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function SkeletonRows() {
  return (
    <tbody aria-hidden="true" className="block md:table-row-group">
      {Array.from({ length: 8 }).map((_, index) => (
        <tr key={index} className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-2 border-b border-line-2 px-4 py-3 md:table-row md:p-0">
          <td className="md:h-[52px] md:px-4"><div className="h-3 w-10 animate-pulse rounded bg-surface-2" /></td>
          <td className="md:px-3"><div className="flex items-center gap-2"><div className="h-6 w-6 animate-pulse rounded-full bg-surface-2" /><div className="h-3 w-20 animate-pulse rounded bg-surface-2" /></div></td>
          <td className="col-span-2 md:px-3"><div className="h-3 w-3/4 animate-pulse rounded bg-surface-2" /><div className="mt-2 h-2.5 w-1/2 animate-pulse rounded bg-surface-2" /></td>
          <td className="hidden px-3 md:table-cell"><div className="h-5 w-16 animate-pulse rounded bg-surface-2" /></td>
          <td className="hidden px-3 md:table-cell"><div className="ml-auto h-3 w-12 animate-pulse rounded bg-surface-2" /></td>
          <td className="hidden md:table-cell" />
        </tr>
      ))}
    </tbody>
  );
}

/* --------------------------------------------------------------------------------------------- */
function StepUndo({ entry, step, busyKey, onRestoreStep }) {
  if (!step?.restorable || entry.status !== 'available') return null;
  return (
    <Button
      type="button"
      size="xs"
      variant="ghost"
      onClick={() => onRestoreStep(entry, step)}
      disabled={busyKey === `${entry.id}:${step.id}`}
      className="shrink-0 opacity-0 focus-visible:opacity-100 group-hover/line:opacity-100"
      aria-label={`Undo only: ${step.label}`}
    >
      <RotateCcw aria-hidden="true" /> Undo step
    </Button>
  );
}

const verbSign = (verb) => (verb === 'added' ? '+' : verb === 'removed' || verb === 'deleted' ? '−' : '~');
const verbTone = (verb) => (verb === 'added' ? 'text-status-green-ink' : verb === 'removed' || verb === 'deleted' ? 'text-status-red-ink' : 'text-ink-3');

/** "+ Fan · SPF-01A, SPF-01B, PF-SEF-PF-01 · 3 devices" — the single steps (each with Undo step) one click away. */
function FloorKindLine({ group, entry, busyKey, onRestoreStep }) {
  const [open, setOpen] = useState(false);
  const single = group.lines.length === 1;
  return (
    <li className="text-[12.5px]" data-testid="history-floor-kind">
      <div className="group/line flex min-h-[28px] items-start gap-2">
        <span className={cn('mt-[5px] w-3 shrink-0 text-center font-bold leading-none', verbTone(group.verb))} aria-hidden="true">{verbSign(group.verb)}</span>
        <span className="min-w-0 flex-1 py-[5px] leading-snug text-ink">
          <span className="sr-only">{group.verb} </span>
          {group.type ? <b className="font-semibold">{group.type}: </b> : null}
          {group.names.join(', ')}
          <span className="text-ink-3"> · {group.countText}</span>
        </span>
        {single ? (
          <StepUndo entry={entry} step={group.lines[0].step} busyKey={busyKey} onRestoreStep={onRestoreStep} />
        ) : (
          <button
            type="button"
            onClick={() => setOpen((current) => !current)}
            aria-expanded={open}
            className="mt-[3px] inline-flex shrink-0 items-center gap-0.5 rounded px-1 text-[11.5px] font-semibold text-ink-3 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Each <ChevronDown className={cn('h-3 w-3 transition-transform', open && 'rotate-180')} aria-hidden="true" />
          </button>
        )}
      </div>
      {open && !single ? (
        <ul className="ml-5 border-l border-line-2 pl-3">
          {group.lines.map((line) => (
            <li key={line.step.id || line.step.label} className="group/line flex min-h-[26px] items-center gap-2">
              <span className="min-w-0 flex-1 truncate text-ink-2">{line.name}</span>
              <StepUndo entry={entry} step={line.step} busyKey={busyKey} onRestoreStep={onRestoreStep} />
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function FloorBox({ group, entry, busyKey, onRestoreStep, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const byType = new Map();
  group.lines.forEach((line) => byType.set(line.kind, (byType.get(line.kind) || 0) + 1));
  const meta = [...byType.entries()].map(([kind, count]) => `${count} ${kind}${count === 1 ? '' : 's'}`).join(' · ');
  return (
    <div className="rounded-xl border border-line px-3 py-2.5" data-testid="history-floor">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        className="flex w-full items-start gap-2 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-x-1.5 text-[13px] font-semibold text-ink">
            {group.floor ? <>Floor - {group.floor}</> : 'Building'}
            {group.panel ? <><ChevronRight className="h-3 w-3 text-ink-4" aria-hidden="true" /> {group.panel}</> : null}
            {group.newPanel ? <StatusPill tone="navy" label="new panel" size="sm" hideDot className="ml-1" /> : null}
          </span>
          {!open ? <span className="mt-0.5 block text-[12px] text-ink-3">{meta || 'No device changes'}</span> : null}
        </span>
        <ChevronDown className={cn('mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-3 transition-transform', open && 'rotate-180')} aria-hidden="true" />
      </button>
      {open ? (
        <ul className="mt-1.5 space-y-1" data-testid="history-floor-lines">
          {groupFloorLines(group.lines).map((kindGroup) => (
            <FloorKindLine key={kindGroup.key} group={kindGroup} entry={entry} busyKey={busyKey} onRestoreStep={onRestoreStep} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** 30 #6 — details beside the feed (no scrim): an inline panel on wide screens, a side panel otherwise. */
function HistoryDrawer({ inline, open, onOpenChange, entry, loading, busyKey, onRestore, onRestoreStep, onLock, values }) {
  const grouped = useMemo(() => groupHistorySteps(entry?.steps || []), [entry]);
  const workspace = useWorkspace();
  // Opened from the top of the feed the panel starts mid-screen: its height is what is left below its top, so
  // "Lock restore point / Undo this change…" are on screen without scrolling the page.
  const asideRef = useRef(null);
  const [fitHeight, setFitHeight] = useState(0);
  const visible = Boolean(inline && open && (entry || loading));
  useEffect(() => {
    if (!visible) return undefined;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const node = asideRef.current;
      if (!node || !node.getBoundingClientRect().height) return;
      const top = Math.max(node.getBoundingClientRect().top, 0);
      setFitHeight(Math.max(320, Math.floor(window.innerHeight - top - 16)));
    };
    const schedule = () => { if (!frame) frame = window.requestAnimationFrame?.(measure) || 0; };
    measure();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (frame) window.cancelAnimationFrame?.(frame);
    };
  }, [visible, entry?.id]);
  if (!open || (!entry && !loading)) return null;
  const sentence = entry ? historySentence(entry) : { lead: 'Loading…', target: '' };
  const Icon = AREA_ICONS[entry?.page_type] || HistoryIcon;
  const iconTone = AREA_ICON_TONES[entry?.page_type] || 'gray';
  const subtitle = entry ? [sentence.target, `${formatDateTime(entry.created_at, { timeZone: workspace.time_zone })} ${workspace.time_zone_short}`, entry.user_name].filter(Boolean).join(' · ') : '';
  const hasValues = Boolean(values?.fields?.length);
  const countBadges = entry && !grouped.badges.length
    ? parseChangeCounts(entry.change_summary).map((part) => ({
      key: `${part.collection}-${part.verb}`,
      tone: part.verb === 'added' ? 'green' : part.verb === 'deleted' ? 'red' : 'gray',
      text: `${part.verb === 'added' ? '+' : part.verb === 'deleted' ? '−' : ''}${part.count} ${collectionNoun(part.collection, part.count)}${part.verb === 'updated' ? ' updated' : ''}`,
    }))
    : grouped.badges;
  const restorable = entry?.status === 'available';
  const anyStructure = grouped.groups.length > 0;
  const verbHeading = grouped.groups.some((group) => group.lines.some((line) => line.verb !== 'added')) ? 'What changed' : 'What was added';

  const footer = entry ? (
    <footer className="flex flex-wrap items-center gap-2 border-t border-line-2 bg-surface px-4 py-3 sm:px-5">
      <Button type="button" variant="ghost" onClick={() => onLock(entry)} disabled={!restorable || busyKey === entry.id} data-testid="history-lock">
        <Lock aria-hidden="true" /> Lock restore point
      </Button>
      <Button type="button" variant="danger" className="ml-auto" onClick={() => onRestore(entry)} disabled={!restorable || busyKey === entry.id} data-testid="history-undo">
        <RotateCcw aria-hidden="true" /> Undo this change…
      </Button>
    </footer>
  ) : null;

  const body = loading && !entry ? (
    <div className="space-y-3" aria-busy="true" aria-label="Loading the change">
      {[0, 1, 2].map((index) => <div key={index} className="h-14 animate-pulse rounded-xl bg-surface-2" />)}
    </div>
  ) : (
    <div className="space-y-4" data-testid="history-detail">
      <div className="flex flex-wrap gap-1.5" data-testid="history-detail-badges">
        {countBadges.map((badge) => <StatusPill key={badge.key} tone={badge.tone} label={badge.text} size="sm" hideDot />)}
        <AreaTag area={entry.page_type} />
        {entry.action_type ? <StatusPill tone="outline" label={optionLabel(HISTORY_ACTIONS, entry.action_type)} size="sm" hideDot /> : null}
      </div>
      {!anyStructure && !hasValues ? (
        <p className="text-[13px] text-ink-2">{humanizeChangeSummary(entry.change_summary) || 'No summary recorded.'}</p>
      ) : null}
      {!anyStructure ? <ValueChanges values={values} timeZone={workspace.time_zone} /> : null}
      {entry.blocked_reason ? <p className="rounded-lg bg-status-red-bg px-3 py-2 text-[12.5px] text-status-red-ink">{entry.blocked_reason}</p> : null}

      {anyStructure ? (
        <div className="space-y-2">
          <UpperLabel>{verbHeading}</UpperLabel>
          {grouped.groups.map((group, index) => (
            <FloorBox key={group.key} group={group} entry={entry} busyKey={busyKey} onRestoreStep={onRestoreStep} defaultOpen={index < 2} />
          ))}
        </div>
      ) : null}

      {grouped.others.length && !(hasValues && !anyStructure && grouped.others.every((step) => step.kind === 'field_updated')) ? (
        <div className="space-y-2">
          <UpperLabel>{anyStructure ? 'Other changes' : 'Records'}</UpperLabel>
          <ul className="divide-y divide-line-2 rounded-xl border border-line">
            {grouped.others.map((step) => (
              <li key={step.id} className="group/line flex items-start gap-2 px-3 py-2">
                <span className="min-w-0 flex-1">
                  <span className="block text-[12.5px] font-medium text-ink">{step.label}</span>
                  {step.record_label ? <span className="block text-[11.5px] text-ink-3">{step.record_label}</span> : null}
                  {step.blocked_reason ? <span className="mt-0.5 block text-[11.5px] text-status-red-ink">{step.blocked_reason}</span> : null}
                </span>
                <HistoryStatus status={step.status} />
                <StepUndo entry={entry} step={step} busyKey={busyKey} onRestoreStep={onRestoreStep} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {!grouped.others.length && !anyStructure && entry.scope_details?.length ? (
        <ul className="space-y-1 text-[12.5px] text-ink-2">
          {entry.scope_details.map((scope) => (
            <li key={scope.collection}>{humanizeChangeSummary(`${scope.collection}: ${scope.summary}`) || scope.summary}</li>
          ))}
        </ul>
      ) : null}

      {grouped.hidden ? (
        <p className="text-[12px] text-ink-3" data-testid="history-hidden-fields">
          {grouped.hidden} system {grouped.hidden === 1 ? 'field' : 'fields'} hidden ({[...new Set(grouped.hiddenNames)].slice(0, 3).join(', ')})
        </p>
      ) : null}
      {restorable ? (
        <p className="text-[12px] text-ink-3">“Undo this change” puts these records back as they were before. Later changes to the same records may block it.</p>
      ) : null}
    </div>
  );

  if (inline) {
    return (
      <aside
        ref={asideRef}
        className="sticky top-[calc(var(--app-header-height)+16px)] flex max-h-[calc(100vh-var(--app-header-height)-32px)] w-[460px] shrink-0 flex-col self-start overflow-hidden rounded-card border border-line bg-surface shadow-raised"
        style={fitHeight ? { maxHeight: fitHeight } : undefined}
        aria-label="Change details"
        onKeyDown={(event) => { if (event.key === 'Escape') onOpenChange(false); }}
        data-testid="history-drawer"
      >
        <header className="flex items-start gap-3 border-b border-line-2 px-5 py-4">
          <span className={cn('ui-icon-tile h-9 w-9', toneClass(iconTone))} aria-hidden="true"><Icon className="h-[18px] w-[18px]" /></span>
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <h3 className="min-w-0 font-heading text-[16px] font-extrabold leading-tight text-ink">{sentence.lead}</h3>
              {entry ? <HistoryStatus status={entry.status} always /> : null}
            </div>
            {subtitle ? <p className="mt-1 text-[12.5px] text-ink-3">{subtitle}</p> : null}
          </div>
          <Button type="button" variant="ghost" size="icon-sm" onClick={() => onOpenChange(false)} aria-label="Close details" className="text-ink-3" data-testid="history-drawer-close">
            <X aria-hidden="true" />
          </Button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{body}</div>
        {footer}
      </aside>
    );
  }

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      modal={false}
      width="md"
      icon={Icon}
      iconTone={iconTone}
      title={sentence.lead}
      subtitle={subtitle}
      status={entry ? <HistoryStatus status={entry.status} always /> : null}
      testId="history-drawer"
      footer={footer}
    >
      {body}
    </SidePanel>
  );
}

/* --------------------------------------------------------------------------------------------- */
// The CSV rules (formula-safe cells) live in adminModel with the other pure History rules.
export { historyCsv };

export default function HistoryTab({ filters, setFilters, users = [], exportCommand }) {
  const confirm = useConfirm();
  const [searchInput, setSearchInput] = useState(filters.search || '');
  const [groupBy, setGroupBy] = useState('time');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [openBursts, setOpenBursts] = useState(() => new Set());
  const [busyKey, setBusyKey] = useState('');
  const [values, setValues] = useState({});
  const [detailValues, setDetailValues] = useState(null);
  const [insights, setInsights] = useState(null);
  const [insightsTick, setInsightsTick] = useState(0);
  const requestRef = useRef(0);
  const detailRequestRef = useRef(0);
  const askedValues = useRef(new Set());
  const workspace = useWorkspace();
  const offsetMinutes = workspace.utc_offset_minutes ?? 240;
  const dayTimeZone = workspace.time_zone || '';

  // 30 #5 / #9 — the period's totals (per area, per person), patterns, changes per project and project names: one
  // counted request over exactly the feed's window (since = its date_from), again after an undo or a lock
  const insightDays = INSIGHT_DAYS[filters.range] || 14;
  const insightsSince = filters.range ? historyRangeStart(filters.range) : '';
  useEffect(() => {
    let alive = true;
    api.get('/admin/history/insights', { params: { days: insightDays, ...(insightsSince ? { since: insightsSince } : {}) } })
      .then((res) => { if (alive) setInsights(res.data || null); })
      .catch(() => { if (alive) setInsights(null); });
    return () => { alive = false; };
  }, [insightDays, insightsSince, insightsTick]);

  // 30 #8 — before → after for the loaded entries (asked once per entry, in batches)
  useEffect(() => {
    const missing = items.map((item) => item.id).filter((id) => id && !askedValues.current.has(id));
    if (!missing.length) return undefined;
    let alive = true;
    missing.forEach((id) => askedValues.current.add(id));
    const batches = [];
    for (let index = 0; index < missing.length; index += VALUES_BATCH) batches.push(missing.slice(index, index + VALUES_BATCH));
    Promise.all(batches.map((ids) => api.post('/admin/history/values', { ids }).then((res) => res.data?.values || {}).catch(() => ({}))))
      .then((results) => { if (alive) setValues((current) => Object.assign({}, current, ...results)); });
    return () => { alive = false; };
  }, [items]);

  // Search waits until typing pauses (each request scans the whole history).
  useEffect(() => {
    if ((filters.search || '') === searchInput) return undefined;
    const timer = setTimeout(() => setFilters((current) => ({ ...current, search: searchInput })), 300);
    return () => clearTimeout(timer);
  }, [searchInput, filters.search, setFilters]);
  useEffect(() => {
    setSearchInput((current) => (current.trim() === (filters.search || '').trim() ? current : filters.search || ''));
  }, [filters.search]);

  const load = useCallback(async () => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/undo/history', { params: historyQueryParams({ ...filters, dayOffsetMinutes: offsetMinutes, dayTimeZone }) });
      if (requestRef.current !== requestId) return;
      const page = res.data?.items || [];
      setItems(page);
      setHasMore(page.length >= HISTORY_PAGE_SIZE);
    } catch (err) {
      if (requestRef.current !== requestId) return;
      setItems([]);
      setHasMore(false);
      setError(getErrorMessage(err, 'Could not load the history'));
    } finally {
      if (requestRef.current === requestId) setLoading(false);
    }
  }, [filters, offsetMinutes, dayTimeZone]);

  useEffect(() => { load(); }, [load]);

  const loadOlder = async () => {
    const oldest = items[items.length - 1];
    if (!oldest) return;
    setLoadingOlder(true);
    try {
      const res = await api.get('/undo/history', { params: historyQueryParams({ ...filters, dayOffsetMinutes: offsetMinutes, dayTimeZone }, { before: oldest.created_at }) });
      const page = res.data?.items || [];
      setItems((current) => mergeHistoryPages(current, page));
      setHasMore(page.length >= HISTORY_PAGE_SIZE);
    } catch (err) {
      notify.error('Could not load older changes', { description: getErrorMessage(err) });
    } finally {
      setLoadingOlder(false);
    }
  };

  // Header "Export CSV": the changes loaded with the current filters.
  const lastExport = useRef(exportCommand);
  useEffect(() => {
    if (exportCommand === undefined || exportCommand === lastExport.current) return;
    lastExport.current = exportCommand;
    if (!items.length) {
      notify.info('Nothing to export', { description: 'No changes match these filters.' });
      return;
    }
    try {
      const blob = new Blob([`﻿${historyCsv(withProjectNames(items, insights?.project_names), { zoneShort: workspace.time_zone_short })}`], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `history-${toDateInputValue(new Date())}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      notify.success(`Exported ${items.length} ${items.length === 1 ? 'change' : 'changes'}`);
    } catch (err) {
      notify.error('Could not export the history', { description: getErrorMessage(err) });
    }
  }, [exportCommand, items, insights, workspace.time_zone_short]);

  const setFilter = (key, value) => setFilters((current) => ({ ...current, [key]: value }));

  const openDetail = async (entryId) => {
    const requestId = detailRequestRef.current + 1;
    detailRequestRef.current = requestId;
    setSelectedId(entryId);
    setDetailLoading(true);
    setDetail((current) => (current?.id === entryId ? current : null));
    setDetailValues((current) => (current?.id === entryId ? current : null));
    api.get(`/admin/history/${entryId}/values`)
      .then((res) => { if (detailRequestRef.current === requestId) setDetailValues(res.data || null); })
      .catch(() => { if (detailRequestRef.current === requestId) setDetailValues(null); });
    try {
      const res = await api.get(`/undo/history/${entryId}`);
      if (detailRequestRef.current !== requestId) return;
      setDetail(res.data?.item ? withProjectNames([res.data.item], insights?.project_names)[0] : null);
    } catch (err) {
      if (detailRequestRef.current !== requestId) return;
      setSelectedId('');
      notify.error('Could not load the change', { description: getErrorMessage(err) });
    } finally {
      if (detailRequestRef.current === requestId) setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    detailRequestRef.current += 1;
    setSelectedId('');
    setDetail(null);
    setDetailLoading(false);
  };
  const closeDetailRef = useRef(closeDetail);
  closeDetailRef.current = closeDetail;

  const restore = async (entry) => {
    const { lead, target } = historySentence(entry);
    const ok = await confirm({
      title: 'Undo this change?',
      subtitle: `${lead}${target ? ` — ${target}` : ''} · ${entry.user_name} · ${formatDateTime(entry.created_at, { timeZone: workspace.time_zone })} ${workspace.time_zone_short}`,
      description: 'The data goes back to how it was before this change. Later changes to the same records may block it.',
      tone: 'danger',
      confirmLabel: 'Undo change',
    });
    if (!ok) return;
    setBusyKey(entry.id);
    try {
      await api.post(`/undo/history/${entry.id}/restore`);
      notify.success('Change undone', { description: lead });
      setInsightsTick((n) => n + 1);
      await load();
      if (selectedId === entry.id) await openDetail(entry.id);
    } catch (err) {
      notify.error('Could not undo the change', { description: getErrorMessage(err) });
    } finally {
      setBusyKey('');
    }
  };

  const restoreStep = async (entry, step) => {
    const ok = await confirm({
      title: 'Undo only this step?',
      subtitle: step.label,
      description: 'Only this record goes back; the rest of the change stays.',
      confirmLabel: 'Undo step',
    });
    if (!ok) return;
    setBusyKey(`${entry.id}:${step.id}`);
    try {
      const res = await api.post(`/undo/history/${entry.id}/steps/${step.id}/restore`);
      notify.success('Step undone');
      setInsightsTick((n) => n + 1);
      setDetail(res.data?.item || detail);
      await load();
    } catch (err) {
      notify.error('Could not undo the step', { description: getErrorMessage(err) });
    } finally {
      setBusyKey('');
    }
  };

  const lock = async (entry) => {
    const ok = await confirm({
      title: 'Lock this restore point?',
      subtitle: historySentence(entry).lead,
      description: 'Nothing changes now — this change just can’t be undone from History any more.',
      confirmLabel: 'Lock restore point',
    });
    if (!ok) return;
    setBusyKey(entry.id);
    try {
      const res = await api.post(`/undo/history/${entry.id}/cancel`);
      notify.success('Restore point locked');
      setDetail((current) => (current?.id === entry.id ? res.data?.item || current : current));
      await load();
    } catch (err) {
      notify.error('Could not lock the restore point', { description: getErrorMessage(err) });
    } finally {
      setBusyKey('');
    }
  };

  const openDetailRef = useRef(openDetail);
  openDetailRef.current = openDetail;
  const onOpen = useCallback((item) => { openDetailRef.current(item.id); }, []);
  const onToggleBurst = useCallback((key) => {
    setOpenBursts((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const namedItems = useMemo(() => withProjectNames(items, insights?.project_names), [items, insights]);
  const groups = useMemo(() => groupHistory(namedItems, groupBy), [namedItems, groupBy]);
  // "Show these changes": that person on that day — for an undo pattern, only their undos
  const showPattern = useCallback((pattern) => {
    setSearchInput('');
    setFilters({
      search: '', person: pattern.user_id || '', area: '', action: pattern.kind === 'undo_churn' ? 'restore' : '', status: '', range: '',
      day: pattern.day || '',
    });
  }, [setFilters]);
  const statusSummary = historyStatusSummary(items);
  const oldest = items[items.length - 1];
  const rangeLabel = optionLabel(HISTORY_DATE_RANGES, filters.range);
  // the summary strip names the period it counts: the chosen day, else the date range
  const dayLabel = filters.day ? formatDate(filters.day, { weekday: true, year: 'auto' }).replace(',', '') : '';
  const periodLabel = dayLabel || rangeLabel;
  // the whole period (counted on the server) when the feed shows a date range filtered at most by ONE of the strip's
  // own area / person (the strip then leads with that slice) — a search, action or status filter, one day, or a
  // person AND an area are cuts the totals cannot express, so the loaded rows are counted instead
  const periodTotals = filters.range && !filters.day && !filters.search && !filters.action && !filters.status
    && !(filters.person && filters.area) && insights?.since === insightsSince ? insightsBreakdown(insights) : null;
  const personOptions = useMemo(
    () => users.map((user) => ({ value: user.id, label: user.name })).sort((a, b) => a.label.localeCompare(b.label)),
    [users]
  );
  const sliceLabel = filters.person
    ? personOptions.find((option) => option.value === filters.person)?.label
      || periodTotals?.people.find((person) => person.id === filters.person)?.name || ''
    : filters.area ? historyAreaLabel(filters.area) : '';
  const anyFilter = Boolean(filters.search || filters.person || filters.area || filters.action || filters.status || filters.day);
  const drawerOpen = Boolean(selectedId);
  const wide = useMediaQuery('(min-width: 1440px)');
  // Esc closes the inline drawer wherever the focus is (a row keeps the focus after a click) — unless a dialog is open
  useEffect(() => {
    if (!wide || !drawerOpen) return undefined;
    const onKey = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (document.querySelector('[role="alertdialog"], [role="dialog"][data-state="open"], [role="menu"]')) return;
      closeDetailRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [wide, drawerOpen]);
  const compact = wide && drawerOpen;

  return (
    <div className="space-y-4">
      <h2 className="sr-only">Audit History</h2>

      {!error && !loading ? (
        <SummaryStrip
          items={items}
          label={periodTotals ? periodLabel : hasMore ? 'Loaded so far' : `${periodLabel || 'All time'}${filters.search || filters.action || filters.status ? ' · matching' : ''}`}
          period={periodTotals}
          since={insightsSince}
          filters={filters}
          sliceLabel={sliceLabel}
          onArea={(value) => setFilter('area', value)}
          onPerson={(value) => setFilter('person', value)}
        />
      ) : null}

      {!error && !filters.day ? <PatternCallout patterns={insights?.patterns || []} onShow={showPattern} /> : null}

      <div className="flex flex-wrap items-center gap-2" data-testid="history-toolbar">
        <SearchBox
          value={searchInput}
          onChange={setSearchInput}
          placeholder="Search project, item or person"
          label="Search history by project, item or person"
          className="w-full sm:w-[256px]"
          shortcut=""
          testId="history-search"
        />
        {filters.day ? (
          <FilterChip
            label="Day"
            value={dayLabel}
            onClear={() => setFilters((current) => ({ ...current, day: '', range: '14d' }))}
            data-testid="history-chip-day"
          />
        ) : (
          <ChipMenu label="Date" value={filters.range} options={HISTORY_DATE_RANGES} onChange={(value) => setFilter('range', value)} allLabel="Any time" testId="history-chip-date" />
        )}
        <ChipMenu label="Person" value={filters.person} options={personOptions} onChange={(value) => setFilter('person', value)} allLabel="Everyone" testId="history-chip-person" />
        <ChipMenu label="Area" value={filters.area} options={HISTORY_AREAS} onChange={(value) => setFilter('area', value)} allLabel="All areas" testId="history-chip-area" />
        <ChipMenu label="Action" value={filters.action} options={HISTORY_ACTIONS} onChange={(value) => setFilter('action', value)} allLabel="All actions" testId="history-chip-action" />
        <ChipMenu label="Status" value={filters.status} options={HISTORY_STATUSES} onChange={(value) => setFilter('status', value)} allLabel="Any status" testId="history-chip-status" />
        <Segmented
          label="Group by"
          value={groupBy}
          onChange={setGroupBy}
          testIdPrefix="history-group"
          className="ml-auto"
          options={[{ value: 'time', label: 'Time' }, { value: 'project', label: 'Project' }, { value: 'person', label: 'Person' }]}
        />
      </div>

      {/* Wide screens: the details sit beside the feed (Area folds into the What column) so Time / Who / What stay readable. */}
      <div className="flex items-start gap-4">
      <div className="min-w-0 flex-1 overflow-hidden rounded-card border border-line bg-surface shadow-card">
        {error ? (
          <EmptyState layout="inline" tone="red" icon={HistoryIcon} title="Couldn’t load the history" text={error} action={<Button variant="outline" onClick={load}>Try again</Button>} />
        ) : !loading && !items.length ? (
          <EmptyState
            icon={SearchX}
            title={anyFilter || filters.range ? 'No changes match these filters' : 'No changes recorded yet'}
            text={filters.range ? `Nothing in the ${rangeLabel.toLowerCase()}${anyFilter ? ' with these filters' : ''}.` : 'Try removing a filter.'}
            action={(anyFilter || filters.range) ? (
              <Button variant="outline" onClick={() => { setSearchInput(''); setFilters((current) => ({ ...current, search: '', person: '', area: '', action: '', status: '', range: '' })); }}>
                Clear filters
              </Button>
            ) : null}
          />
        ) : (
          <div className="md:overflow-x-auto">
            {/* Phones: every row folds into a two-line card (time · who · › / what changed) */}
            <table className={cn('block w-full border-collapse md:table md:table-fixed', !compact && 'md:min-w-[720px]')} data-testid="history-table" aria-busy={loading}>
              <colgroup>
                <col className={compact ? 'w-[84px]' : 'w-[96px]'} />
                <col className={compact ? 'w-[116px]' : 'w-[170px]'} />
                <col />
                {compact ? null : <col className="w-[120px]" />}
                <col className={compact ? 'w-[96px]' : 'w-[140px]'} />
                <col className="w-[32px]" />
              </colgroup>
              <thead className="hidden md:table-header-group">
                <tr className="border-b border-line">
                  <th scope="col" className="h-9 whitespace-nowrap bg-surface-2 px-4 text-left text-[11.5px] font-semibold text-ink-3">Time{compact ? null : <span className="font-normal"> ({workspace.time_zone_short})</span>}</th>
                  <th scope="col" className="h-9 bg-surface-2 px-3 text-left text-[11.5px] font-semibold text-ink-3">Who</th>
                  <th scope="col" className="h-9 bg-surface-2 px-3 text-left text-[11.5px] font-semibold text-ink-3">What changed{compact ? <span className="font-normal"> · area</span> : null}</th>
                  {compact ? null : <th scope="col" className="h-9 bg-surface-2 px-3 text-left text-[11.5px] font-semibold text-ink-3">Area</th>}
                  <th scope="col" className="h-9 bg-surface-2 px-3 text-right text-[11.5px] font-semibold text-ink-3">Size</th>
                  <th scope="col" className="h-9 bg-surface-2"><span className="sr-only">Open</span></th>
                </tr>
              </thead>
              {loading ? <SkeletonRows /> : (
                <HistoryValuesContext.Provider value={values}>
                <HistoryRows
                  groups={groups}
                  groupBy={groupBy}
                  selectedId={selectedId}
                  openBursts={openBursts}
                  onToggleBurst={onToggleBurst}
                  onOpen={onOpen}
                  compact={compact}
                />
                </HistoryValuesContext.Provider>
              )}
            </table>
          </div>
        )}
        {!error && items.length ? (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-4 py-3 text-[12.5px] text-ink-3" data-testid="history-footer">
            <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>
                {hasMore
                  ? `Showing the ${items.length} most recent changes (since ${formatDate(oldest?.created_at, { weekday: true, year: 'auto' }).replace(',', '')})`
                  : filters.range
                    ? `All ${items.length} ${items.length === 1 ? 'change' : 'changes'} from the ${rangeLabel.toLowerCase()}`
                    : `All ${items.length} ${items.length === 1 ? 'change' : 'changes'} · since ${formatDate(oldest?.created_at)}`}
                {groupBy === 'time' && !loading ? ` · ${groups.reduce((sum, group) => sum + burstHistory(group.items).length, 0)} events` : ''}
              </span>
              {statusSummary && !loading ? (
                <>
                  <span aria-hidden="true">·</span>
                  {/* one line instead of a Status column; exceptions also get a badge on their row */}
                  <span className="inline-flex items-center gap-1.5 whitespace-nowrap font-semibold text-ink-2" data-testid="history-status-summary">
                    <RotateCcw className="h-[13px] w-[13px]" aria-hidden="true" /> {statusSummary}
                  </span>
                </>
              ) : null}
            </span>
            {hasMore ? (
              <Button type="button" variant="outline" size="sm" onClick={loadOlder} disabled={loadingOlder} data-testid="history-load-older">
                {loadingOlder ? 'Loading…' : 'Load older'}
              </Button>
            ) : filters.range ? (
              <Button type="button" variant="ghost" size="sm" onClick={() => setFilter('range', '')} data-testid="history-include-older">
                <Plus aria-hidden="true" /> Include older changes
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      <HistoryDrawer
        inline={wide}
        open={drawerOpen}
        onOpenChange={(next) => { if (!next) closeDetail(); }}
        entry={detail}
        loading={detailLoading}
        busyKey={busyKey}
        onRestore={restore}
        onRestoreStep={restoreStep}
        onLock={lock}
        values={detailValues && detailValues.id === selectedId ? detailValues : null}
      />
      </div>
    </div>
  );
}
